import { useEffect, useState, startTransition } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  Avatar,
  Badge,
  Flex,
  Menu,
  Tooltip,
  Kbd,
  UnstyledButton,
} from "@mantine/core";
import { useSpotlight } from "@mantine/spotlight";
import {
  AiOutlineLogout,
  AiOutlineSetting,
  AiOutlineShareAlt,
  AiOutlineUsergroupAdd,
  AiOutlineUserSwitch,
  AiOutlineAppstore,
} from "react-icons/ai";
import { IconInfoCircle, IconSearch } from "@tabler/icons-react";

import AiAssistantIcon from "components/AiAssistantIcon/AiAssistantIcon";
import Logo from "assets/logo.svg";
import { useFloatingMicAssistant } from "components/FloatingMicAssistant/FloatingMicAssistantContext";
import InviteModal from "components/InviteModal/InviteModal";
import IntegrationsModal from "components/IntegrationsModal/IntegrationsModal";
import WithPremium from "components/WithPremium/WithPremium";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { useUser } from "stores/useUser";
import useWorkspaceStore from "stores/useWorkspaceStore";
import { canUseFloatingAssistant } from "utils/assistantAccess";
import { notify } from "utils/common";
import { FirebaseAuth, FirestoreDB } from "utils/firebase";
import { resetPostHogUser } from "utils/posthog";
import {
  ASSISTANT_NAME,
  ASSISTANT_SHORT_NAME,
} from "utils/assistantIdentity";

import css from "./Header.module.scss";
import ShareAccessDrawer from "components/ShareAccessDrawer/ShareAccessDrawer";
import WithRole from "components/WithRole/WithRole";
import { impersonate, stopImpersonation } from "api";
import { useMutation } from "react-query";
import ImpersonateUserModal from "components/ImpersonateUserModal/ImpersonateUserModal";
import ImpersonationSwitchModal from "components/ImpersonationSwitchModal/ImpersonationSwitchModal";
import { SHARED_ACTIONS_VISIBLE } from "components/Actions/SharedActions/visibility";

// Mantine Menu.Item with optional lock + tooltip. When `locked`, the
// click is swallowed (preventDefault + stopPropagation, so the Menu also
// doesn't auto-close) and a hover tooltip explains why. Used to disable
// nav-style actions in the profile menu while the user is in a meeting.
const LockableMenuItem = ({
  locked,
  lockMessage,
  onClick,
  children,
  ...rest
}) => {
  const handleClick = (event) => {
    if (locked) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };
  const item = (
    <Menu.Item
      {...rest}
      onClick={handleClick}
      style={
        locked
          ? { opacity: 0.45, cursor: "not-allowed", ...(rest.style || {}) }
          : rest.style
      }
      closeMenuOnClick={!locked}
      aria-disabled={locked || undefined}
    >
      {children}
    </Menu.Item>
  );
  if (!locked) return item;
  return (
    <Tooltip label={lockMessage} withArrow position="right">
      {/* span wrapper so Tooltip's hover handlers attach even though the
          inner Menu.Item is click-suppressed. inline-block so layout
          inside the dropdown is unaffected. */}
      <span style={{ display: "block" }}>{item}</span>
    </Tooltip>
  );
};

const Header = () => {
  const [inviteModalOpened, setInviteModalOpened] = useState(false);
  const [impersonateModalOpened, setImpersonateModalOpened] = useState(false);
  const [integrationsModalOpened, setIntegrationsModalOpened] = useState(false);
  const navItems = ["workspace", "actions", "insights", "calendar"];
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const spotlight = useSpotlight();
  const { openChat } = useFloatingMicAssistant();
  const [connCount, setConnCount] = useState(0);
  const [shareAccessOpened, setShareAccessOpened] = useState(false);
  const [isImpersonated, setIsImpersonated] = useState(false);
  const [switchModalOpened, setSwitchModalOpened] = useState(false);
  const [switchErrorMessage, setSwitchErrorMessage] = useState("");
  // Check if we're on the workspace page
  const isWorkspacePage = location.pathname === "/workspace";
  // const canOpenAssistant = canUseFloatingAssistant(user?.email);
  // While the user is inside an embedded Zoom meeting we disable nav so
  // they can't accidentally navigate away mid-call. Set/cleared by
  // WorkspacePage based on activeEmbeddedMeeting.
  const inEmbeddedMeeting = useWorkspaceStore(
    (state) => state.inEmbeddedMeeting,
  );
  const NAV_LOCK_MESSAGE =
    "During the meeting you cannot navigate to other pages";
  // Helper function to navigate with startTransition
  const handleNavigate = (path) => {
    startTransition(() => {
      navigate(path);
    });
  };

  const inviteHandler = () => {
    //assuming now maximum connnections 15
    if (connCount < 15) {
      setInviteModalOpened(true);
    } else {
      notify("Your connections limit has been reached !!", "error");
    }
  };

  // here getting count of connections
  const getConnectionsCount = async () => {
    const q = query(
      collection(FirestoreDB, "users", user?.uid, "connections"),
      where("uid", "!=", null),
    );
    onSnapshot(q, (querySnapshot) => {
      setConnCount(querySnapshot.size);
    });
  };

  useEffect(() => {
    if (user?.subscription?.plan) {
      getConnectionsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if user is impersonated by checking custom claims
  useEffect(() => {
    const checkImpersonation = async () => {
      if (user) {
        try {
          const idTokenResult =
            await FirebaseAuth.currentUser?.getIdTokenResult();
          setIsImpersonated(!!idTokenResult?.claims?.impersonated);
        } catch (error) {
          console.error("Error checking impersonation:", error);
          setIsImpersonated(false);
        }
      } else {
        setIsImpersonated(false);
      }
    };
    checkImpersonation();
  }, [user]);

  const { mutate: impersonateUser, isLoading: isImpersonating } = useMutation({
    mutationFn: (userId) => impersonate(userId),
    onSuccess: async (data) => {
      await signInWithCustomToken(FirebaseAuth, data?.data?.token).then(
        async (user) => {
          await Promise.resolve(setUser(user.user));
        },
      );
      setImpersonateModalOpened(false);
      notify("Successfully impersonated user", "success");
      navigate("/");
    },
    onError: (error) => {
      console.log("Error", error);
      notify("Error impersonating user", "error");
    },
  });

  const {
    mutate: stopImpersonationUser,
    isLoading: isStoppingImpersonation,
  } = useMutation({
    mutationFn: () => stopImpersonation(),
    onSuccess: async (data) => {
      await signInWithCustomToken(FirebaseAuth, data?.data?.token).then(
        async (user) => {
          await Promise.resolve(setUser(user.user));
        }
      );
      setSwitchModalOpened(false);
      setSwitchErrorMessage("");
      notify("Switched back to original profile", "success");
      handleNavigate("/");
    },
    onError: (error) => {
      console.log("Error", error);
      setSwitchErrorMessage(
        error?.message || "Error switching back to original profile."
      );
      notify("Error switching back to original profile", "error");
    },
  });

  const handleImpersonate = (selectedUser) => {
    // This will be called from the modal when user clicks impersonate button
    // For now, just call the impersonateUser mutation
    // When backend is ready, pass the selectedUser data to the impersonate function
    impersonateUser(selectedUser.uid);
  };

  return (
      <header className={css.container}>
        <div className={css.logo} onClick={() => handleNavigate("/workspace")}>
          <img src={Logo} alt="" />
          <span>Gain</span>
          <span>Time</span>
        </div>

        <ul className={css.menu}>
          {navItems.map((item, i) => {
            // When locked, intercept the click so react-router doesn't
            // navigate. We still render the NavLink (preserves active
            // styling on the current page) but it becomes a no-op with
            // a tooltip explaining why.
            const handleClick = (event) => {
              if (inEmbeddedMeeting) event.preventDefault();
            };
            const linkProps = {
              to: `/${item}`,
              className: ({ isActive }) => {
                const classes = [];
                if (isActive) classes.push(css.active);
                if (inEmbeddedMeeting) classes.push(css.disabled);
                return classes.join(" ") || undefined;
              },
              onClick: handleClick,
              "aria-disabled": inEmbeddedMeeting || undefined,
            };
            const linkNode =
              item === "insights" ? (
                <WithPremium feature={"insights"}>
                  <NavLink {...linkProps}>
                    {item}
                    <div className="tab-bottom-line" />
                  </NavLink>
                </WithPremium>
              ) : (
                <NavLink {...linkProps}>
                  {item}
                  <div className="tab-bottom-line" />
                </NavLink>
              );
            return (
              <li key={i}>
                {inEmbeddedMeeting ? (
                  <Tooltip
                    label={NAV_LOCK_MESSAGE}
                    withArrow
                    position="bottom"
                  >
                    {/* span wrapper so Tooltip can attach hover handlers
                        even while the NavLink itself is click-disabled */}
                    <span style={{ display: "inline-block" }}>{linkNode}</span>
                  </Tooltip>
                ) : (
                  linkNode
                )}
              </li>
            );
          })}
        </ul>

        {isWorkspacePage && (
          <UnstyledButton
            className={css.searchButton}
            onClick={() => spotlight.openSpotlight()}
          >
            <Flex align="center" gap={8}>
              <IconSearch size={18} />
              <span>Search</span>
              <Kbd>{navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl"}</Kbd>
              <Kbd>K</Kbd>
            </Flex>
          </UnstyledButton>
        )}

        {/* {canOpenAssistant && ( */}
          <Tooltip label={`Open ${ASSISTANT_NAME}`} withArrow position="bottom">
            <UnstyledButton
              className={`${css.aiAssistantButton} ${
                !isWorkspacePage ? css.aiAssistantButtonNoSearch : ""
              }`}
              aria-label={`Open ${ASSISTANT_NAME}`}
              onClick={openChat}
            >
              <AiAssistantIcon className={css.aiAssistantIcon} />
              <span>{ASSISTANT_SHORT_NAME}</span>
            </UnstyledButton>
          </Tooltip>
        {/* )} */}

        <Menu>
          <div className={css.profileContainer}>
            <Menu.Target>
              <Flex gap={"0.75rem"} align="center">
                {isImpersonated && (
                  <Tooltip
                    label="You are impersonating another user and only have read-only access"
                    withArrow
                    position="bottom"
                  >
                    <Badge
                      color="orange"
                      variant="filled"
                      style={{ cursor: "help" }}
                      size="sm"
                    >
                      <Flex gap={6}>
                        <IconInfoCircle size={14} /> <span>Impersonating</span>
                      </Flex>
                    </Badge>
                  </Tooltip>
                )}
                <span>{user.displayName.split(" ")[0]}</span>
                <Avatar
                  src={user?.photoURL}
                  alt="profile img"
                  radius="xl"
                  size={"28px"}
                />
              </Flex>
            </Menu.Target>
            <Menu.Dropdown>
              {user?.subscription?.plan.toUpperCase() === "INDIVIDUAL" && (
                <Menu.Item
                  icon={<AiOutlineUsergroupAdd size="20px" />}
                  onClick={inviteHandler}
                >
                  Invite others
                </Menu.Item>
              )}
              <WithRole allowedRoles={["admin"]}>
                <LockableMenuItem
                  locked={inEmbeddedMeeting}
                  lockMessage={NAV_LOCK_MESSAGE}
                  onClick={() => handleNavigate("/organization")}
                  icon={<AiOutlineSetting size="20px" />}
                >
                  Manage Org
                </LockableMenuItem>
              </WithRole>

              <LockableMenuItem
                locked={inEmbeddedMeeting}
                lockMessage={NAV_LOCK_MESSAGE}
                icon={<AiOutlineAppstore size={20} />}
                onClick={() => setIntegrationsModalOpened(true)}
              >
                <span>Integrations</span>
              </LockableMenuItem>

              {SHARED_ACTIONS_VISIBLE && (
                <Menu.Item
                  icon={<AiOutlineShareAlt size={20} />}
                  onClick={() => setShareAccessOpened(true)}
                >
                  <span>Share Actions access</span>
                </Menu.Item>
              )}

              {isImpersonated ? (
                <LockableMenuItem
                  locked={inEmbeddedMeeting}
                  lockMessage={NAV_LOCK_MESSAGE}
                  icon={<AiOutlineUserSwitch size={20} />}
                  disabled={isStoppingImpersonation}
                  onClick={() => {
                    setSwitchErrorMessage("");
                    setSwitchModalOpened(true);
                    stopImpersonationUser();
                  }}
                >
                  <span>Switch to Original Profile</span>
                </LockableMenuItem>
              ) : (
                <WithRole allowedRoles={["admin"]}>
                  <LockableMenuItem
                    locked={inEmbeddedMeeting}
                    lockMessage={NAV_LOCK_MESSAGE}
                    icon={<AiOutlineUserSwitch size={20} />}
                    onClick={() => setImpersonateModalOpened(true)}
                  >
                    <span>Impersonate User</span>
                  </LockableMenuItem>
                </WithRole>
              )}

              <Menu.Item
                icon={<AiOutlineLogout size="20px" />}
                onClick={() => {
                  signOut(FirebaseAuth)
                    .then(async () => {
                      resetPostHogUser();
                      await Promise.resolve(setUser(null));
                      // Full reload (not client-side navigate) so any
                      // COOP/COEP isolation picked up from /workspace
                      // is dropped before the next popup login.
                      window.location.assign("/login");
                    })
                    .catch((error) => {
                      notify("an error happened while signing out", "error");
                      console.log(error);
                    });
                }}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </div>
          {/* modal component */}
          <InviteModal
            opened={inviteModalOpened}
            setOpened={setInviteModalOpened}
          />
          <IntegrationsModal
            opened={integrationsModalOpened}
            setOpened={setIntegrationsModalOpened}
          />
        </Menu>
        <ShareAccessDrawer
          opened={shareAccessOpened}
          setOpened={setShareAccessOpened}
        />
        <ImpersonateUserModal
          opened={impersonateModalOpened}
          setOpened={setImpersonateModalOpened}
          onImpersonate={handleImpersonate}
          isImpersonating={isImpersonating}
        />
        <ImpersonationSwitchModal
          opened={switchModalOpened}
          isLoading={isStoppingImpersonation}
          errorMessage={switchErrorMessage}
          onClose={() => setSwitchModalOpened(false)}
          onRetry={() => {
            setSwitchErrorMessage("");
            stopImpersonationUser();
          }}
        />
      </header>
  );
};

export default Header;
